import React, { useState } from 'react';
import {
  Bell,
  CircleAlert,
  Gamepad2,
  GlobeLock,
  History,
  Info,
  LocateFixed,
  Mail,
  MapPinned,
  Monitor,
  MonitorCog,
  MonitorSmartphone,
  ShieldAlert,
  Tv,
  UserRoundSearch,
  VenetianMask,
  Wifi,
} from 'lucide-react';

const SmartParenting = () => {
  const availableFeatures = [
    {
      icon: Monitor,
      title: 'Screentime Management',
      description: 'Balance their screen time. Protect their childhood.',
    },
    {
      icon: Gamepad2,
      title: 'App & Game Control',
      description: 'You decide what belongs in their digital world.',
    },
    {
      icon: GlobeLock,
      title: 'Block Pornography & Explicit Content',
      description: 'No hidden browsing. No unwanted searches. More parental control.',
    },
    {
      icon: Wifi,
      title: 'Real-Time Data Sync',
      description: 'What happens now. Know it now.',
    },
    {
      icon: LocateFixed,
      title: 'Real-Time Location Tracking',
      description:'Know where they are. When it matters most.',
    },
    {
      icon: UserRoundSearch,
      title: 'Social Media Monitoring & Control',
      description: 'Their social world. Your choice.',
    },
    {
      icon: Mail,
      title: 'Call History Access',
      description: 'Notice the signs. Start the conversation. Be there when they need you. Because sometimes, a pattern of calls can reveal a problem they can’t say out loud.',
    },
    {
      icon: CircleAlert,
      title: 'Alerts & Reports',
      description: 'When something needs your attention, you’ll know.',
    },
    {
      icon: Bell,
      title: 'App Download Notifications',
      description: 'Know what’s being added to their digital world.',
    },
    {
      icon: Tv,
      title: 'Remotely Control Smart TV',
      description: 'When screen time ends, the screen ends too.',
    },
  ];
  const upcomingFeatures = [
    {
      icon: History,
      title: 'Browsing History Logs',
      description: 'Because sometimes, their search history speaks before they do.',
    },
    {
      icon: ShieldAlert,
      title: 'SOS',
      description: 'Because in an emergency, every second matters.',
    },
    {
      icon: MonitorSmartphone,
      title: 'Realtime Screen View',
      description: 'See what they see. Be there when it matters.',
    },
    {
      icon: MonitorCog,
      title: 'Screen Auto Adjustment',
      description: 'Protect their eyes today. Help preserve healthy vision for tomorrow.',
    },
    {
      icon: VenetianMask,
      title: 'Spam & False Information Filtering',
      description: 'Keep the noise out. Help genuine information stand out.',
    },
    {
      icon: MapPinned,
      title: 'Geo-Fencing Zones',
      description: 'Set their safe zones. Know when they step beyond them.',
    },
  ];
  const [selected, setSelected] = useState('available');

  return (
    <section className='section bg-white'>
      <div className='w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
        <h2 className='text-[rgb(131,202,201)] uppercase tracking-widest text-2xl sm:text-3xl md:text-4xl leading-8 mb-4 text-center'>
          Key Features Overview
        </h2>
        <h2 className='section-title text-center text-teal-500 text-xl sm:text-2xl md:text-3xl'>
          Smart Parenting Starts with Smart Features
        </h2>
        <p className='section-subtitle text-center text-[#595959] text-base sm:text-lg mb-6'>
          Explore powerful tools designed to protect, monitor, and guide your
          child’s digital habits.
        </p>
        <div className='flex flex-col sm:flex-row items-center sm:items-end gap-4 sm:gap-8 mb-8 justify-center'>
          <button
            className={`text-lg sm:text-xl font-semibold border-b-2 pb-1 min-w-[140px] sm:min-w-[180px] md:min-w-[220px] transition-colors ${
              selected === 'available'
                ? 'text-teal-500 border-teal-300'
                : 'text-gray-700 border-transparent'
            }`}
            onClick={() => setSelected('available')}
          >
            Available Features
          </button>
          <button
            className={`text-lg sm:text-xl font-semibold border-b-2 pb-1 min-w-[140px] sm:min-w-[180px] md:min-w-[220px] transition-colors ${
              selected === 'upcoming'
                ? 'text-teal-500 border-teal-300'
                : 'text-gray-700 border-transparent'
            }`}
            onClick={() => setSelected('upcoming')}
          >
            Upcoming Features
          </button>
        </div>
        <div className='flex flex-col gap-8'>
          {selected === 'available' && (
            <div className='flex flex-col gap-5 w-full'>
              <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6 flex-1 justify-center'>
                {availableFeatures.slice(0, 10).map((feature, index) => (
                  <FeatureTile
                    key={index}
                    icon={feature.icon}
                    title={feature.title}
                    description={feature.description}
                    active={index % 2 === 0}
                  />
                ))}
              </div>
            </div>
          )}
          {selected === 'upcoming' && (
            <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6 flex-1 justify-center'>
              {upcomingFeatures.map((feature, index) => (
                <FeatureTile
                  key={index}
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                  active={index % 2 === 0}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default SmartParenting;

/**
 * Common tile component for feature display, styled as in the screenshot.
 */
export const FeatureTile = ({ icon: Icon, title, description, active }) => {
  const [open, setOpen] = useState(false);

  return (
    <div
      className='relative'
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <div
        role='button'
        tabIndex={0}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((prev) => !prev);
          }
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        aria-label={`${title}. ${description}`}
        aria-expanded={open}
        className={`flex flex-col items-center justify-center rounded-xl border transition-all duration-300 cursor-pointer outline-none
          ${
            active
              ? 'bg-[#E9FAF9] border-[#DEF7F6] shadow-md'
              : 'bg-white border-[#DEF7F6] hover:shadow-lg'
          }
          p-8 min-h-[170px] max-h-[170px] sm:w-full md:min-w-[220px] md:max-w-[220px] text-center`}
      >
        <div
          className={`flex items-center justify-center mb-6 rounded-lg
            ${active ? '' : 'bg-[#eafafa]'}
            p-2 text-3xl`}
        >
          <Icon
            className='w-12 h-12 text-teal-600'
            aria-hidden='true'
            strokeWidth={1.8}
          />
        </div>
        <div className='text-gray-700 text-lg font-medium text-center leading-tight'>
          {title}
        </div>
      </div>

      {description && open && (
        <div
          role='tooltip'
          className='absolute bottom-full left-1/2 z-20 mb-4 w-[min(20rem,calc(100vw-2rem))] -translate-x-1/2 overflow-hidden rounded-2xl border border-teal-100 bg-white text-left text-gray-700 shadow-[0_16px_40px_rgba(13,148,136,0.2)] pointer-events-none animate-[tooltipIn_180ms_ease-out]'
        >
          <div className='h-1 bg-gradient-to-r from-teal-400 via-cyan-400 to-teal-500' />
          <div className='flex items-start gap-3 px-4 py-4'>
            <span className='mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-teal-50 text-teal-600'>
              <Info className='h-4 w-4' strokeWidth={2.5} aria-hidden='true' />
            </span>
            <span>
              <span className='block text-[11px] font-bold uppercase tracking-[0.16em] text-teal-600'>
                Why it matters
              </span>
              <span className='mt-1 block text-sm font-semibold leading-snug text-gray-900'>
                {title}
              </span>
              <span className='mt-1.5 block text-sm leading-relaxed text-gray-600'>
                {description}
              </span>
            </span>
          </div>
          <span className='absolute left-1/2 top-full h-3 w-3 -translate-x-1/2 -translate-y-1/2 rotate-45 border-b border-r border-teal-100 bg-white' />
        </div>
      )}
    </div>
  );
};
